import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { useAuthContext } from 'AuthProvider'
import { AgGridReact } from 'ag-grid-react'
import { EditStatusCellRenderer, LoadingOverlay } from 'components/commonparts/index'
import { useCustomer } from 'hooks/useCustomer'
import { useGridEdit } from 'hooks/useGridEdit'
import { initRecordEditEx, type DataListPropsBase, type RecordEditEx } from 'types/dataGridTypes'
import { validateNumberLength, validateRequired } from 'utils/validation'

import type { ColDef, ColGroupDef } from 'ag-grid-enterprise'
import type { Customer, MstCustomer, GetMstCustomersCustomerGetRequest } from 'api-client'
import 'utils/mst.scss'

const columnMapping: Record<string, string> = {
  org_customer_id: 'orgCustomerId',
  customer_name: 'customerName',
  customer_hojin_no: 'customerHojinNo',
  customer_address: 'customerAddress',
  customer_gln: 'customerGln',
  internal_transfer_flg: 'internalTransferFlg',
  remarks: 'remarks',
  delete_flg: 'deleteFlg',
}
const primayKey = ['orgCustomerId']

interface DataListProps extends DataListPropsBase {
  list: Customer
  searchParam: GetMstCustomersCustomerGetRequest
  gridRef?: React.RefObject<AgGridReact>
  findSearchValue?: string
  onFindChanged?: (event: any) => void
  isSearchVisible?: boolean
}

interface MstCustomerWithId extends MstCustomer, RecordEditEx {}

function DataList({
  list,
  isLoading,
  searchParam,
  onReload,
  updateTrigger,
  onUploadData,
  setHasGridEdited,
  setResultMessage,
  setDownloadColumnDefs,
  gridRef: externalGridRef,
  findSearchValue: externalFindSearchValue,
  onFindChanged: externalOnFindChanged,
  isSearchVisible = false,
}: DataListProps) {
  const { loginUserInfo } = useAuthContext()
  const { fetchCustomerUpdate, isUpdating, result, customerSearchResult } = useCustomer()
  const internalGridRef = useRef<AgGridReact<MstCustomerWithId>>(null)
  const gridRef = externalGridRef || internalGridRef
  const containerStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  
  const gridStyle = useMemo(() => {
    const searchBoxHeight = isSearchVisible ? 40 : 0
    return { width: '100%', height: `calc(100vh - ${120 + searchBoxHeight}px)` }
  }, [isSearchVisible])
  const [row, setRow] = useState<MstCustomerWithId[]>(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))

  useEffect(() => {
    setResultMessage(result)
  }, [result])

  useEffect(() => {
    setResultMessage(customerSearchResult)
  }, [customerSearchResult])
  
  const createBlankRow = useCallback((): MstCustomerWithId => {
    const blankRow: Partial<MstCustomerWithId> = {
      companyId: loginUserInfo?.companyId || null,
      orgCustomerId: null,
      customerAddress: null,
      customerName: null,
      customerHojinNo: null,
      customerGln: null,
      customerCompanyId: null,
      remarks: null,
      deleteFlg: false,
      insertDatetime: null,
      updateDatetime: null,
      insertUser: loginUserInfo?.userId || null,
      deleteDatetime: null,
      deleteUser: null,
      updateUser: null,
      internalTransferFlg: false,
      ...initRecordEditEx(true),
    }
    return blankRow as MstCustomerWithId
  }, [])

  // 入力チェック
  const fieldValidate = (pRow: MstCustomerWithId, field: string | undefined, newValue: any): boolean => {
    let hasErrors = false
    switch (field) {
      case 'orgCustomerId': {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        if (!hasErrors) {
          hasErrors = duplicateCheck(pRow, newValue)
        }
        break
      }
      case 'customerName': {
        hasErrors = checkAndSetError(pRow, field, validateRequired(newValue))
        break
      }
      case 'customerHojinNo': {
        hasErrors = checkAndSetError(pRow, field, validateNumberLength(newValue, 13).join(','))
        break
      }
      case 'customerGln': {
        hasErrors = checkAndSetError(pRow, field, validateNumberLength(newValue, 9).join(','))
        break
      }
      case 'internalTransferFlg': {
        if (newValue === true) {
          // Trueに設定できるのは1レコードのみ
          const existingTrueRecord = row.find(r => 
            r.id !== pRow.id && 
            r.internalTransferFlg === true
          )
          if (existingTrueRecord) {
            hasErrors = checkAndSetError(pRow, field, "Trueに設定できるのは1レコードのみです")
          }
        }
        break
      }
      default:
        return false
    }
    // console.log('fieldValidate', field, pRow, hasErrors)
    return hasErrors
  }

  const updateMstCustomer = useCallback(
    async (updatedRows: MstCustomerWithId[]) => {
      const datalist = updatedRows.map(({ isEdited, editedFields, errors, ...rest }) => ({
        ...rest,
      }))

      try {
        await fetchCustomerUpdate({
          mstCustomerUpdate: { body: datalist },
        })
        if (onReload) onReload()
      } catch (err: any) {
        if (err?.errors && err.result?.status === 'error') {
          const gridApi = gridRef.current?.api

          err.errors.forEach((errrow: any) => {
            const { rowdata } = errrow
            const orgCustomerId = (rowdata as { org_customer_id: string | null })?.org_customer_id
            const errRow = updatedRows.find((rowdatas) => rowdatas.orgCustomerId === orgCustomerId)
            if (errRow) {
              // UpdateAPIのErrorは、PKをエラー表記して、サーバーから取得したエラーを表示する
              errRow.errors = { orgCustomerId: [errrow.errorMessage] }
              const rowNode = gridApi?.getRowNode(errRow.id || '')
              if (rowNode) {
                rowNode.setData(errRow)
              }
            }
          })
        }
      }
    },
    [fetchCustomerUpdate, searchParam]
  )

  const handleImportExcel = (data: any[]) => {
    let hasError = false
    const mappedData = data.map((excelRow: any) => {
      const mappedRow: Partial<MstCustomerWithId> = {}
      Object.keys(columnMapping).forEach((key) => {
        if (key in excelRow) {
          const tableField = columnMapping[key as keyof typeof columnMapping]
          const importvalue = excelRow[key]
          let setvalue = importvalue || null

          if (
            ['deleteFlg', 'internalTransferFlg'].includes(tableField)
          ) {
            setvalue = parseBoolean(importvalue)
          } else {
            setvalue = importvalue ? String(importvalue).trim() : null
          }
          // パース出来ないときはスキップ（＝NULL）し、警告メッセージを表示
          if (!isNonInputValue(importvalue) && setvalue === null) hasError = true
          mappedRow[tableField as keyof MstCustomerWithId] = setvalue as unknown as undefined
        }
      })
      return mappedRow as MstCustomerWithId
    })

    // Validate that only one record can have internalTransferFlg = true
    const trueRecords = mappedData.filter(record => record.internalTransferFlg === true)
    if (trueRecords.length > 1) {
      hasError = true
    }

    return { mappedData, hasError }
  }

  const {
    booleanFormatter,
    dateFormatter,
    excelStyles,
    selectValueFormatter,
    typecodeFormatter,
    formatBooleanValue,
    formatDecimalValue,
    isNonInputValue,
    numberValueFormatter,
    numberValueParser,
    parseBoolean,
    parseBooleanValue,
    parseDate,
    parseDateValue,
    parseDecimalValue,
    parseNumeric,
    parsePositiveInteger,
    parseStringValue,
    parseTime,
    selectTypecodeValueParser,
    selectValueParser,
    typeCodeParser,
    localeText,
    autoSizeStrategy,
    onGridReady,
    getCellClassRules,
    checkAndSetError,
    rowDataValidate,
    colDataValidate,
    duplicateCheck,
    isEnableEdit,
    longTextEditorParams,
    longTextValueFormatter,
    longTextValueParser,
    longTextValueSetter,
    editInfoValueGetter,
    clearPinnedRows,
  } = useGridEdit({
    gridRef,
    isLoading,
    isUpdating,
    onReload,
    onUploadData,
    updateTrigger,
    setHasGridEdited,
    setResultMessage,
    columnMapping,
    primayKey,
    storageKey: 'gridColumnState:/master-customer',
    updateData: updateMstCustomer,
    handleImportExcel,
    createBlankRow,
    fieldValidate,
    scrollPositionKey: 'orgCustomerId',
    rowData: row,
  })

  /**
   * カラム定義
   * @returns
   */
  const updateColumnDefs = (): (ColDef | ColGroupDef)[] => 
  [
    {
      headerName: '!',
      rowDrag: true,
      cellRenderer: EditStatusCellRenderer,
      valueGetter: (params) => editInfoValueGetter(params),
      pinned: 'left',
      width: 100,
      filter: 'agMultiColumnFilter',
      filterParams: {
        filters: [
          {
            filter: 'agSetColumnFilter',
            filterParams: {
              values: ['入力エラー', '変更', null],
              refreshValuesOnOpen: true,
              applyMiniFilterWhileTyping: true,
              excelMode: 'mac',
            },
          },
        ],
        buttons: ['reset'],
        closeOnApply: true,
        applyMiniFilterWhileTyping: true,
        excelMode: 'mac',
      },
    },
    {
      headerName: '得意先企業ID',
      field: 'orgCustomerId',
      editable: (params) => isEnableEdit(params.data && params.data.isNewRecord),
      cellClassRules: {
        ...getCellClassRules(),
        'editable-cell': (params) => isEnableEdit(params.data && params.data.insertDatetime === null),
      },
      pinned: 'left',
      width: 200,
    },
    {
      headerName: '得意先企業名',
      field: 'customerName',
      editable: isEnableEdit(),
      pinned: 'left',
      width: 300,
      valueParser: parseStringValue,
    },
    {
      headerName: '会社法人等番号（登記所）',
      field: 'customerHojinNo',
      editable: isEnableEdit(),
      flex: 2,
      valueParser: parseStringValue,
    },
    {
      headerName: '本社住所',
      field: 'customerAddress',
      editable: isEnableEdit(),
      flex: 1,
      valueParser: parseStringValue,
    },
    {
      headerName: 'GS1事業者コード',
      field: 'customerGln',
      editable: isEnableEdit(),
      flex: 2,
      valueParser: parseStringValue,
    },
    {
      headerName: '社内移送用',
      field: 'internalTransferFlg',
      editable: isEnableEdit(),
      cellClassRules: getCellClassRules(),
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue,
      flex: 1,
    },
    {
      headerName: 'PSI得意先企業ID',
      field: 'customerCompanyId',
      flex: 1,
    },
    {
      headerName: '備考',
      field: 'remarks', 
      editable: isEnableEdit(),
      cellEditorPopup: true,
      cellEditor: 'agLargeTextCellEditor',
      cellEditorParams: longTextEditorParams,
      valueFormatter: longTextValueFormatter,
      valueParser: longTextValueParser,
      valueSetter: longTextValueSetter,
      flex: 1,
    },
    {
      headerName: '削除',
      field: 'deleteFlg',
      editable: isEnableEdit(),
      cellRenderer: 'agCheckboxCellRenderer',
      cellEditor: 'agCheckboxCellEditor',
      valueFormatter: formatBooleanValue,
      valueParser: parseBooleanValue,
      flex: 1,
    },
  ]

  const columnDefs = useMemo<ColDef[]>(updateColumnDefs, [])
  // ダウンロードファイル用のカラム定義
  useEffect(() => {
    if (setDownloadColumnDefs)
      setDownloadColumnDefs([
        { headerName: 'PSI企業ID', children: [{ headerName: 'company_id', field: 'companyId' }] },
        {
          headerName: '得意先企業ID',
          children: [{ headerName: 'org_customer_id', field: 'orgCustomerId', cellClass: 'stringType' }],
        },
        { headerName: '得意先企業名', 
          children: [{ headerName: 'customer_name', field: 'customerName', cellClass: 'stringType' }] },
        {
          headerName: '会社法人等番号（登記所）',
          children: [{ headerName: 'customer_hojin_no', field: 'customerHojinNo', cellClass: 'stringType' }],
        },
        {
          headerName: '本社住所',
          children: [{ headerName: 'customer_address', field: 'customerAddress', cellClass: 'stringType' }],
        },
        {
          headerName: 'GS1事業者コード',
          children: [{ headerName: 'customer_gln', field: 'customerGln', cellClass: 'stringType' }],
        },
        {
          headerName: '社内移送用',
          children: [{ headerName: 'internal_transfer_flg', field: 'internalTransferFlg', cellClass: 'booleanType' }],
        },
        {
          headerName: 'PSI得意先企業ID',
          children: [{ headerName: 'customer_company_id', field: 'customerCompanyId', cellClass: 'stringType' }],
        },
        { headerName: '備考', children: [{ headerName: 'remarks', field: 'remarks', cellClass: 'stringType' }] },
        {
          headerName: '削除',
          children: [{ headerName: 'delete_flg', field: 'deleteFlg', cellClass: 'booleanType' }],
        },
      ])
  }, [setDownloadColumnDefs])

  useEffect(() => {
    if (list.list.length === 0) {
      const blankRow = createBlankRow()
      setRow([blankRow])
    } else {
      setRow(list.list.map((item) => ({ ...item, ...initRecordEditEx() })))
      clearPinnedRows()
    }
  }, [list.list])

  return (
    <div style={{ ...containerStyle, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flexGrow: 1, boxSizing: 'border-box' }}>
        <div style={gridStyle} className="ag-theme-balham">
          <AgGridReact
            rowData={row}
            ref={gridRef}
            getRowId={(params) => params.data.id}
            columnDefs={columnDefs}
            onGridReady={onGridReady}
            localeText={localeText}
            autoSizeStrategy={autoSizeStrategy}
            loadingOverlayComponent={LoadingOverlay}
            findSearchValue={externalFindSearchValue}
            onFindChanged={externalOnFindChanged}
            stopEditingWhenCellsLoseFocus
          />
        </div>
      </div>
    </div>
  )
}

export default DataList
